import React, { memo } from 'react';

import { Typography } from 'antd';

import { useTheme } from '../../../../context/ThemeProvider';
import { DELETED_USER } from '../../../../shared/data';
import { useAppNavigate } from '../../../../shared/hooks';
import { ROUTES } from '../../../../types/routes';
import { ITQShoutoutUser } from '../../../../types/tqConnectTypes/user';

export type ShoutoutHeaderTitleType =
  | 'given'
  | 'received'
  | 'giving-to'
  | 'receiving-from';

const GIVE_TYPES = ['given', 'giving-to'];
// const RECEIVE_TYPES = ['received', 'receiving-from'];

interface Props {
  from?: ITQShoutoutUser;
  to?: ITQShoutoutUser;
  type: ShoutoutHeaderTitleType;
  canOpenProfile?: boolean;
}

const ShoutoutHeaderTitle: React.FC<Props> = ({
  from,
  to,
  type,
  canOpenProfile,
}) => {
  const navigate = useAppNavigate();

  const { colors: COLORS } = useTheme();

  const goToUserProfile = (userId?: string | null) => {
    if (!userId || !canOpenProfile) return;

    navigate(ROUTES.USER_PROFILE, { userId });
  };

  if (!from && !to) {
    return null;
  }

  const renderStart = () => {
    // case 1: if type is given or giving-to,
    // show who gave or is giving a shoutout to
    if (GIVE_TYPES.includes(type)) {
      return (
        <Typography.Text
          strong
          style={{
            color: COLORS.TEXT1,
            cursor: from?._id ? 'pointer' : 'auto',
          }}
          onClick={() => goToUserProfile(from?._id)}>
          {from?.name || DELETED_USER}
        </Typography.Text>
      );
    }

    // case 2: if type is received or receiving-from,
    // show who received or is receiving a shoutout from
    return (
      <Typography.Text
        strong
        style={{
          color: COLORS.TEXT1,
          cursor: to?._id ? 'pointer' : 'auto',
        }}
        onClick={() => goToUserProfile(to?._id)}>
        {to?.name || DELETED_USER}
      </Typography.Text>
    );
  };

  const renderMiddle = () => {
    // case 3: if type is given or giving-to,
    // show whether they gave or are giving a shoutout to
    if (GIVE_TYPES.includes(type)) {
      return (
        <Typography.Text style={{ fontWeight: 400, color: COLORS.TEXT1 }}>
          {type === 'given' ? 'gave' : 'is giving'} a shoutout to
        </Typography.Text>
      );
    }

    // case 4: if type is received or receiving-from,
    // show whether they received or are receiving a shoutout from
    return (
      <Typography.Text style={{ fontWeight: 400, color: COLORS.TEXT1 }}>
        {type === 'received' ? 'received' : 'is receiving'} a shoutout from
      </Typography.Text>
    );
  };

  const renderEnd = () => {
    // case 5: if type is given or giving-to,
    // show to whom they gave or are giving a shoutout
    if (GIVE_TYPES.includes(type)) {
      return (
        <Typography.Text
          strong
          style={{
            color: COLORS.TEXT1,
            cursor: to?._id ? 'pointer' : 'auto',
          }}
          onClick={() => goToUserProfile(to?._id)}>
          {to?.name || DELETED_USER}
        </Typography.Text>
      );
    }

    // case 6: if type is received or receiving-from,
    // show from whom they received or are receiving a shoutout
    return (
      <Typography.Text
        strong
        style={{
          color: COLORS.TEXT1,
          cursor: from?._id ? 'pointer' : 'auto',
        }}
        onClick={() => goToUserProfile(from?._id)}>
        {from?.name || DELETED_USER}
      </Typography.Text>
    );
  };

  return (
    <>
      {renderStart()} {renderMiddle()} {renderEnd()}
    </>
  );
};

export default memo(ShoutoutHeaderTitle);
